/**
 * 배차 화면의 **복사 클립보드** — `Ctrl+C` 로 담고 `Ctrl+V` 로 붙인다(2026-08-02 사용자 요청).
 *
 * 왜 OS 클립보드가 아닌가
 *   붙여 넣을 것은 글자가 아니라 **접수·노선이라는 행**이다. OS 클립보드로 실어 나르려면
 *   읽기 권한(`clipboard-read`)을 받아야 하고, 사용자가 그사이 다른 것을 복사하면 조용히
 *   엉뚱한 값이 들어온다. 앱 안에 두면 **무엇이 담겼는지 화면에 계속 보여줄 수 있다.**
 *
 * ⚠️ **`localStorage` 에 둔다** — 접수를 복사하고 다른 화면을 거쳐 돌아오는 동선이 실제로 있다.
 *    메모리에만 두면 그 사이에 사라져 "복사가 안 된다"가 된다. 정산년월 기억과 같은 판단.
 * ⚠️ 담기는 것은 **id 와 이름표뿐**이다. 실제 값은 붙여 넣는 시점에 서버가 원본에서 다시 읽는다 —
 *    복사해 둔 뒤 원본이 바뀌면 **바뀐 것이 붙는다.** 옛 스냅샷을 몰래 붙이는 것보다 낫다.
 */

const KEY = 'businweb.clip';

export interface Clip {
  /** 'recv' = 접수 통째 · 'dispt' = 고른 노선만. 붙일 때 기본 선택이 달라진다. */
  kind: 'recv' | 'dispt';
  recvId: number;
  /** 'dispt' 일 때 그 노선들. 비면 접수의 전 노선이다. */
  disptIds?: number[];
  /** 화면에 보여줄 이름표 — 무엇을 들고 있는지 안 보이면 엉뚱한 곳에 붙인다. */
  label: string;
  /** 언제 담았는지. 오래된 클립을 그대로 붙이는 사고를 화면이 알릴 수 있게. */
  at: string;
}

export function readClip(): Clip | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const c = JSON.parse(raw) as Clip;
    // 모양이 깨졌으면 없는 것으로 친다 — 반쯤 맞는 클립이 제일 위험하다.
    if (!c || (c.kind !== 'recv' && c.kind !== 'dispt') || !Number.isInteger(c.recvId)) return null;
    return c;
  } catch {
    return null;
  }
}

export function writeClip(c: Omit<Clip, 'at'>): Clip {
  const full: Clip = { ...c, at: new Date().toISOString() };
  try {
    localStorage.setItem(KEY, JSON.stringify(full));
  } catch {
    /* 저장이 막혀도 이번 붙여넣기는 되게 둔다 */
  }
  return full;
}

export function clearClip() {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* 무시 */
  }
}

/**
 * 키 입력을 **가로채도 되는 자리인가**.
 *
 * 🔴 입력칸 안에서 누른 `Ctrl+C` 는 **글자 복사**여야 한다. 이걸 안 가르면
 *    검색어를 복사하려던 사람이 접수를 복사하게 되고, 그 사실을 알 방법이 없다.
 */
export function isTypingTarget(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null;
  if (!el || !el.tagName) return false;
  if (el.isContentEditable) return true;
  return ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName);
}

/** 글자를 선택해 둔 상태면 그쪽이 우선이다 — 사용자는 그걸 복사하려던 것이다. */
export function hasTextSelection(): boolean {
  const s = window.getSelection();
  return !!s && !s.isCollapsed && String(s).trim() !== '';
}
